<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import Repositories from '@/repositories';
const ImagesRepository = Repositories.get('images');

import ConfirmationModal from '@/components/widgets/modals/confirmation-modal';

export default {
  props: {
    repository: { type: Object, required: true },
    objectId: { type: Number, default: 0 },
    placement: { type: String, default: 'left' },
    target: { type: String, required: true },
    baseSharedUrl: { type: String, required: true },
    baseSharedKey: { type: String },
    title: { type: String },
    description: { type: String },
  },
  components: {
    Loading,
    ConfirmationModal,
  },
  data() {
    return {
      isLoading: false,
      isCopied: false,
      pop: false,
      sharedKey: null,
    };
  },
  created() {
    this.sharedKey = this.baseSharedKey;
  },
  methods: {
    showPopover() {
      this.isCopied = false;

      if (this.sharedUrl == null) {
        this.generateNewLink();
      }
    },
    async generateNewLink() {
      let newKey = this.generateRandomString(12);

      await this.patchSharedUrlKey(newKey);
    },
    async patchSharedUrlKey(key) {
      try {
        this.isLoading = true;

        let patchPayload = [
          {
            op: 'add',
            path: 'sharedUrlKey',
            value: key,
          },
        ];

        await this.repository.patch(this.objectId, patchPayload);

        this.sharedKey = key;
        this.$emit('shared-url-key-updated', this.sharedKey);

        return true;
      } catch (error) {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
      } finally {
        this.isLoading = false;
      }

      return false;
    },
    async deleteSharedUrl() {
      let result = await this.$bvModal.msgBoxConfirm(
        this.$t('general.areYouSureDeleteLink'),
        {
          size: 'sm',
          buttonSize: 'md',
          okVariant: 'danger',
          okTitle: this.$t('general.delete'),
          cancelVariant: 'secondary',
          cancelTitle: this.$t('general.cancel'),
          centered: true,
        }
      );

      if (result == null || result == false) return;

      if (await this.patchSharedUrlKey(null)) {
        this.pop = false;
      }
    },
    generateRandomString(length) {
      const chars =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
      const randomValues = crypto.getRandomValues(new Uint8Array(length));
      return Array.from(randomValues, (byte) => chars[byte & 63]).join('');
    },
    copyLink() {
      navigator.clipboard.writeText(this.sharedUrl);
      this.isCopied = true;
    },
    async getQRCode() {
      if (this.sharedUrl != null) {
        try {
          this.isLoading = true;

          let response = await ImagesRepository.customREST.getQRCode({
            url: this.sharedUrl,
            title: this.title,
            description: this.description,
          });

          let base64Image = 'data:image/jpeg;base64,' + response.data;

          let link = document.createElement('a');
          link.href = base64Image;
          link.download = `${this.title}_${this.description}.jpeg`;
          link.click();
        } catch {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        } finally {
          this.isLoading = false;
        }
      }
    },
    base64ToArrayBuffer(base64) {
      let binaryString = base64;
      let binaryLen = binaryString.length;
      let bytes = new Uint8Array(binaryLen);
      for (let i = 0; i < binaryLen; i++) {
        let ascii = binaryString.charCodeAt(i);
        bytes[i] = ascii;
      }
      return bytes;
    },
  },
  computed: {
    sharedUrl() {
      if (this.sharedKey == null || this.sharedKey.length == 0) return null;

      return `${this.baseSharedUrl}?key=${this.sharedKey}&lang=${this.$i18n.locale}`;
    },
  },
  watch: {
    baseSharedKey() {
      if (this.sharedKey == this.baseSharedKey) return;

      this.sharedKey = this.baseSharedKey;
    },
  },
};
</script>

<template>
  <div>
    <b-button
      :id="target + '-shared-link-btn'"
      :variant="sharedUrl == null ? 'primary' : 'success'"
      class="text-center"
      style="font-size: 1.1em"
      @click="pop = !pop"
    >
      <span class="pr-1">
        {{
          $t(
            sharedUrl == null
              ? 'general.generateSharedUrl'
              : 'general.manageSharedUrl'
          )
        }}
      </span>
      <img
        style="margin-top: -1px"
        width="18px"
        height="18px"
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAKxJREFUSEvVVdEOgCAIhPX/n1w0XTRGoCVq5ZtTuDuQE2HwwsH54QQgInLAFkTcPCJeHCLm3PMBGLm1dKzIVZAOarJL598CYJmaVWnvKpA1f5Iwxen7Mpc5B5Ee6MfxDkDrE7Xi5lmF1eRLPY/xJ6JkHYlcthG979HkPgDeHDDDEnO+czE7aRUtAF2sQvdGKgGANdu0Z9d3zC4MEJmDaomM5D/50SJlKcUOt4odRkXkGW5+V2EAAAAASUVORK5CYII="
      />
    </b-button>

    <b-popover
      :ref="target + '-shared-link-ref'"
      :target="target + '-shared-link-btn'"
      triggers="manual"
      :placement="placement"
      :show="pop"
      @show="showPopover"
    >
      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        color="#f1b44c"
        :width="70"
        :height="70"
        loader="dots"
        :is-full-page="false"
      >
      </loading>

      <div v-if="sharedUrl == null" style="width: 150px; height: 22px">
        {{ $t('') }}
      </div>
      <div v-else>
        <div class="flex-box" style="color: #777">
          <label for="shared-link-input">{{ $t('general.sharedUrl') }}</label>
          <i
            @click="pop = !pop"
            class="flex-push fas fa-times"
            style="font-size: 1.2em; margin-right: 1px; cursor: pointer"
          ></i>
        </div>
        <b-input-group>
          <b-form-input
            id="shared-link-input"
            :value="this.sharedUrl"
          ></b-form-input>
          <b-input-group-append>
            <b-button
              :variant="isCopied ? 'success' : 'primary'"
              @click="copyLink"
            >
              {{ isCopied ? $t('general.copied') : $t('general.copy') }}
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <b-button block class="mt-2" variant="primary" @click="getQRCode">
          {{ $t('general.downloadQRCode') }}
        </b-button>

        <b-button
          block
          class="mt-2"
          variant="outline-danger"
          @click="deleteSharedUrl"
        >
          {{ $t('general.deleteSharedUrl') }}
        </b-button>
      </div>
    </b-popover>

    <ConfirmationModal id="shared-link" ref="confirmationModalRef" />
  </div>
</template>

<style>
.popover {
  z-index: 1040;
}

.modal {
  z-index: 1060;
}
</style>
